// ============================================================================
// COMPONENTE: CadernoTarefas.jsx — C3 (lado admin)
// Criar e acompanhar tarefas atribuídas ao cliente de um caderno.
// Coleção: artifacts/{appId}/public/data/client_tasks
// ============================================================================

import React, { useEffect, useState } from 'react';
import {
  subscribeClientTasks,
  createTask,
  completeTask,
  reopenTask,
  rejectTask,
  deleteTask,
} from './CadernoTarefasRepository';
import { CheckCircle2, Clock, Plus, Trash2, RotateCcw, XCircle } from 'lucide-react';

const TYPE_OPTS = [
  { value: 'document',  label: 'Documento' },
  { value: 'form',      label: 'Formulário' },
  { value: 'signature', label: 'Assinatura' },
  { value: 'presence',  label: 'Comparecer' },
];

const STATUS_LABEL = {
  pending:   { label: 'Pendente',   cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  submitted: { label: 'Enviado',    cls: 'bg-blue-100 text-blue-700 border-blue-300'   },
  completed: { label: 'Concluído',  cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  rejected:  { label: 'Rejeitado',  cls: 'bg-red-100 text-red-700 border-red-300'      },
};

const formatDue = (ts) => {
  if (!ts) return '—';
  try {
    const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

export default function CadernoTarefas({ clientId, workspaceId }) {
  const [tasks, setTasks]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote,  setRejectNote]  = useState('');
  const [notice, setNotice]     = useState('');

  const [fTitle,    setFTitle]    = useState('');
  const [fDesc,     setFDesc]     = useState('');
  const [fType,     setFType]     = useState('document');
  const [fPriority, setFPriority] = useState('normal');
  const [fDueDate,  setFDueDate]  = useState('');

  useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    const unsub = subscribeClientTasks(
      clientId,
      (snap) => {
        setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [clientId]);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 3000); };

  const handleCreate = async () => {
    if (!fTitle.trim() || !clientId) return;
    setSaving(true);
    try {
      await createTask({
        clientId,
        workspaceId:  workspaceId || null,
        title:        fTitle.trim(),
        description:  fDesc.trim(),
        type:         fType,
        priority:     fPriority,
        dueDate:      fDueDate ? new Date(fDueDate) : null,
        status:       'pending',
      });
      setFTitle(''); setFDesc(''); setFType('document'); setFPriority('normal'); setFDueDate('');
      setShowForm(false);
      flash('Tarefa criada com sucesso.');
    } catch { flash('Erro ao criar tarefa.'); }
    setSaving(false);
  };

  const handleComplete = async (task) => {
    try {
      await completeTask(task.id);
      flash('Tarefa marcada como concluída.');
    } catch {}
  };

  const handleReopen = async (task) => {
    try {
      await reopenTask(task.id);
      flash('Tarefa reaberta.');
    } catch {}
  };

  const handleReject = async (task) => {
    if (!rejectNote.trim()) return;
    try {
      await rejectTask(task.id, rejectNote.trim());
      flash('Tarefa devolvida ao cliente.');
    } catch {}
    setRejectingId(null);
    setRejectNote('');
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Excluir tarefa "${task.title}"?`)) return;
    try {
      await deleteTask(task.id);
    } catch {}
  };

  if (!clientId) return (
    <div className="text-xs text-gray-400 italic px-1 py-2">Nenhum cliente vinculado a este caderno.</div>
  );

  return (
    <div className="space-y-3">
      {/* Feedback */}
      {notice && (
        <div className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          {notice}
        </div>
      )}

      {/* Botão + Nova Tarefa */}
      {!showForm && (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center gap-1.5 border border-dashed border-[#C5A059]/60 rounded-lg py-2 text-xs font-bold text-[#C5A059] hover:bg-[#C5A059]/5 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Nova Tarefa para o Cliente
        </button>
      )}

      {/* Formulário inline */}
      {showForm && (
        <div className="border border-[#C5A059]/40 rounded-lg bg-[#C5A059]/5 p-3 space-y-2">
          <input
            type="text"
            value={fTitle}
            onChange={e => setFTitle(e.target.value)}
            placeholder="Título da tarefa *"
            className="w-full text-xs border border-gray-300 rounded-lg px-2.5 py-2 outline-none focus:border-[#20243B] bg-white"
          />
          <textarea
            value={fDesc}
            onChange={e => setFDesc(e.target.value)}
            placeholder="Descrição (opcional)"
            rows={2}
            className="w-full text-xs border border-gray-300 rounded-lg px-2.5 py-2 outline-none focus:border-[#20243B] bg-white resize-none"
          />
          <div className="grid grid-cols-2 gap-2">
            <select value={fType} onChange={e => setFType(e.target.value)} className="text-xs border border-gray-300 rounded-lg px-2 py-2 outline-none focus:border-[#20243B] bg-white">
              {TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={fPriority} onChange={e => setFPriority(e.target.value)} className="text-xs border border-gray-300 rounded-lg px-2 py-2 outline-none focus:border-[#20243B] bg-white">
              <option value="normal">Normal</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 font-medium mb-1 block">Prazo (opcional)</label>
            <input
              type="datetime-local"
              value={fDueDate}
              onChange={e => setFDueDate(e.target.value)}
              className="w-full text-xs border border-gray-300 rounded-lg px-2.5 py-2 outline-none focus:border-[#20243B] bg-white"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={handleCreate}
              disabled={!fTitle.trim() || saving}
              className="flex-1 bg-[#20243B] text-white text-xs font-bold rounded-lg py-2 hover:bg-[#C5A059] hover:text-[#20243B] transition-colors disabled:opacity-40"
            >
              {saving ? 'Criando...' : 'Criar Tarefa'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-3 border border-gray-300 rounded-lg text-xs text-gray-500 hover:bg-gray-100 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista de tarefas */}
      {loading ? (
        <div className="flex items-center gap-2 py-2">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-[#C5A059]" />
          <span className="text-xs text-gray-400">Carregando...</span>
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-xs text-gray-400 italic text-center py-2">Nenhuma tarefa atribuída.</div>
      ) : (
        <div className="space-y-1.5">
          {tasks.map(task => {
            const st = STATUS_LABEL[task.status] || STATUS_LABEL.pending;
            const isActive = task.status === 'pending' || task.status === 'submitted';
            return (
              <div key={task.id} className={`rounded-lg border px-2.5 py-2 flex items-start gap-2 ${isActive ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-70'}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-xs font-bold text-[#20243B] ${!isActive ? 'line-through' : ''}`}>{task.title}</span>
                    <span className={`text-[9px] font-bold border rounded px-1 py-0.5 uppercase ${st.cls}`}>{st.label}</span>
                    {task.priority === 'urgent' && isActive && (
                      <span className="text-[9px] font-bold border rounded px-1 py-0.5 uppercase bg-amber-100 text-amber-700 border-amber-300">Urgente</span>
                    )}
                  </div>
                  {task.description ? <p className="text-[10px] text-gray-500 mt-0.5 truncate">{task.description}</p> : null}
                  <div className="flex items-center gap-1 mt-0.5 text-[10px] text-gray-400">
                    <Clock className="w-2.5 h-2.5" /> {formatDue(task.dueDate)}
                  </div>
                </div>
                <div className="relative flex items-center gap-1 shrink-0">
                  {task.status === 'submitted' && (
                    <>
                      <button type="button" onClick={() => handleComplete(task)} title="Marcar como concluído" className="h-6 w-6 rounded flex items-center justify-center text-emerald-600 hover:bg-emerald-50 border border-emerald-200 transition-colors">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => { setRejectingId(task.id); setRejectNote(''); }} title="Devolver ao cliente" className="h-6 w-6 rounded flex items-center justify-center text-red-500 hover:bg-red-50 border border-red-200 transition-colors">
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {rejectingId === task.id && (
                    <div className="absolute right-0 top-8 z-20 w-56 bg-white border border-red-200 rounded-lg shadow-lg p-2 space-y-1.5">
                      <p className="text-[10px] font-bold text-red-700">Motivo da devolução:</p>
                      <textarea
                        value={rejectNote}
                        onChange={e => setRejectNote(e.target.value)}
                        rows={2}
                        className="w-full text-xs border border-gray-300 rounded px-2 py-1 outline-none focus:border-red-400 resize-none"
                        placeholder="Ex: Documento ilegível, falta assinatura..."
                      />
                      <div className="flex gap-1">
                        <button type="button" onClick={() => handleReject(task)} disabled={!rejectNote.trim()} className="flex-1 text-[10px] font-bold bg-red-500 text-white rounded px-2 py-1 hover:bg-red-600 disabled:opacity-40">Devolver</button>
                        <button type="button" onClick={() => setRejectingId(null)} className="text-[10px] text-gray-500 border border-gray-200 rounded px-2 py-1 hover:bg-gray-50">Cancelar</button>
                      </div>
                    </div>
                  )}
                  {!isActive && (
                    <button type="button" onClick={() => handleReopen(task)} title="Reabrir" className="h-6 w-6 rounded flex items-center justify-center text-gray-500 hover:bg-gray-100 border border-gray-200 transition-colors">
                      <RotateCcw className="w-3 h-3" />
                    </button>
                  )}
                  <button type="button" onClick={() => handleDelete(task)} title="Excluir" className="h-6 w-6 rounded flex items-center justify-center text-red-400 hover:bg-red-50 border border-red-200 transition-colors">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
